import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle, XCircle, RefreshCw, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export function WebhookDiagnostics() {
  const { user, isAdmin } = useAuth();
  const [isChecking, setIsChecking] = useState(false);
  const [webhookStatus, setWebhookStatus] = useState<any>(null);
  const [recentSubscriptions, setRecentSubscriptions] = useState<any[]>([]);

  // Only show for admin users with proper role-based check
  if (!user || !isAdmin) {
    return null;
  }

  const checkWebhookStatus = async () => {
    setIsChecking(true);
    try {
      console.log("Checking webhook status...");
      
      // Check webhook configuration
      const { data: webhookData, error: webhookError } = await supabase.functions.invoke('stripe-webhook', {
        body: { verify: true }
      });

      if (webhookError) {
        console.error("Webhook check error:", webhookError);
        setWebhookStatus({ error: webhookError.message });
      } else {
        console.log("Webhook status:", webhookData);
        setWebhookStatus(webhookData);
      }

      // Get recent subscriptions to see if they have address data
      const { data: subscriptions, error: subError } = await supabase
        .from('subscriptions')
        .select('customer_name, email, address, postcode, created_at, active')
        .order('created_at', { ascending: false })
        .limit(10);

      if (!subError && subscriptions) {
        setRecentSubscriptions(subscriptions);
      }

    } catch (error) {
      console.error("Error checking webhook status:", error);
      toast.error("Failed to check webhook status");
    } finally {
      setIsChecking(false);
    }
  };

  const runFullSync = async () => {
    setIsChecking(true);
    try {
      console.log("Running full Stripe sync...");
      
      const { data, error } = await supabase.functions.invoke('stripe-sync-customers');
      
      if (error) {
        console.error("Sync error:", error);
        toast.error(`Sync failed: ${error.message}`);
      } else {
        console.log("Sync results:", data);
        toast.success(`Sync completed! Processed ${data.customersProcessed || 0} customers`);
        
        // Refresh the data
        await checkWebhookStatus();
      }
    } catch (error) {
      console.error("Error running sync:", error);
      toast.error("Failed to run sync");
    } finally {
      setIsChecking(false);
    }
  };

  const getWebhookUrl = () => {
    return "https://iaydpqbgutssogjfqygl.supabase.co/functions/v1/stripe-webhook";
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Webhook Diagnostics
          </CardTitle>
          <CardDescription>
            Diagnose and fix webhook issues preventing customer data transfer
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Button onClick={checkWebhookStatus} disabled={isChecking}>
              {isChecking ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
              Check Webhook Status
            </Button>
            <Button onClick={runFullSync} variant="outline" disabled={isChecking}>
              Run Full Stripe Sync
            </Button>
            <Button 
              variant="outline" 
              onClick={() => window.open("https://dashboard.stripe.com/webhooks", "_blank")}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Stripe Dashboard
            </Button>
          </div>

          {webhookStatus && (
            <Alert className={webhookStatus.stripe_key_configured && webhookStatus.webhook_secret_configured ? "border-green-200" : "border-red-200"}>
              <AlertDescription>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {webhookStatus.stripe_key_configured ? 
                      <CheckCircle className="h-4 w-4 text-green-600" /> : 
                      <XCircle className="h-4 w-4 text-red-600" />
                    }
                    <span>Stripe API Key: {webhookStatus.stripe_key_configured ? "Configured" : "Missing"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {webhookStatus.webhook_secret_configured ? 
                      <CheckCircle className="h-4 w-4 text-green-600" /> : 
                      <XCircle className="h-4 w-4 text-red-600" />
                    }
                    <span>Webhook Secret: {webhookStatus.webhook_secret_configured ? "Configured" : "Missing"}</span>
                  </div>
                  <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                    <strong>Webhook URL for Stripe:</strong><br/>
                    <code className="text-xs">{getWebhookUrl()}</code>
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <h4 className="font-semibold">Required Webhook Events in Stripe:</h4>
            <div className="grid grid-cols-2 gap-2">
              <Badge variant="outline">checkout.session.completed</Badge>
              <Badge variant="outline">invoice.paid</Badge>
              <Badge variant="outline">customer.subscription.updated</Badge>
              <Badge variant="outline">customer.subscription.deleted</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent Subscriptions Data Check</CardTitle>
          <CardDescription>
            Checking if recent customers have complete address data
          </CardDescription>
        </CardHeader>
        <CardContent>
          {recentSubscriptions.length > 0 ? (
            <div className="space-y-2">
              {recentSubscriptions.map((sub, index) => (
                <div key={index} className="flex items-center justify-between p-2 border rounded">
                  <div>
                    <span className="font-medium">{sub.customer_name}</span>
                    <span className="text-sm text-gray-500 ml-2">({sub.email})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {sub.address ? (
                      <Badge variant="outline" className="text-green-600">
                        Address: {sub.address.substring(0, 20)}...
                      </Badge>
                    ) : (
                      <Badge variant="destructive">No Address</Badge>
                    )}
                    <Badge variant={sub.active ? "default" : "secondary"}>
                      {sub.active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No recent subscriptions found</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Webhook Setup Instructions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-semibold">1. In Stripe Dashboard:</h4>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Go to Developers → Webhooks</li>
              <li>Click "Add endpoint"</li>
              <li>Enter URL: <code className="text-xs bg-gray-100 p-1 rounded">{getWebhookUrl()}</code></li>
              <li>Select events: checkout.session.completed, invoice.paid, customer.subscription.updated, customer.subscription.deleted</li>
              <li>Click "Add endpoint"</li>
              <li>Copy the "Signing secret" and add it to your Supabase secrets as STRIPE_WEBHOOK_SECRET</li>
            </ul>
          </div>
          
          <Alert>
            <AlertDescription>
              <strong>Common Issue:</strong> If webhooks were working before but stopped, check if your webhook endpoint was disabled in Stripe due to failed delivery attempts.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
}